import { useMemo } from "react";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { type SubmitHandler, useForm } from "react-hook-form";
import type { LifeguardLoginFormProps } from "./LifeguardLoginForm.types";
import { vm } from "../../../../shared/utils/validationMessages";

const formSchema = z.object({
  city_id: z
    .string()
    .trim()
    .min(1, { message: vm.CITY_REQUIRED }),
  building_id: z
    .string()
    .trim()
    .min(1, { message: vm.BUILDING_REQUIRED }),
});

type FormSchema = z.infer<typeof formSchema>;

/**
 * Custom hook for LifeguardLoginForm business logic
 * Handles:
 * - Form validation and state
 * - Calculate unique cities from buildings
 * - Filter buildings by selected city
 * - Handle city and building selection
 */
export const useLifeguardLoginForm = ({
  onLogin,
  buildings,
}: Omit<LifeguardLoginFormProps, "isSubmitting">) => {
  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      city_id: "",
      building_id: "",
    },
  });

  const {
    watch,
    setValue,
    handleSubmit,
    formState: { errors, isValid },
  } = form;

  const selectedCityId = watch("city_id");
  const selectedBuildingId = watch("building_id");

  // Calculate unique cities from buildings
  const citiesOptions = useMemo(() => {
    if (!buildings) return [];

    const map = new Map<string, { label: string; value: string }>();

    buildings.forEach((building) => {
      const label = building.city.name;
      const value = String(building.city.id);

      if (!map.has(value)) {
        map.set(value, { label, value });
      }
    });

    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [buildings]);

  // Filter buildings by selected city and transform to options
  const buildingsOptions = useMemo(() => {
    if (!buildings || !selectedCityId) return [];

    const filtered = buildings
      .filter((building) => String(building.city.id) === selectedCityId)
      .map((building) => ({
        value: building.id,
        label: building.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return filtered;
  }, [buildings, selectedCityId]);

  // Reset building_id when city changes
  const handleCityChange = (cityId: string) => {
    setValue("city_id", cityId, { shouldValidate: true });
    setValue("building_id", "", { shouldValidate: false });
  };

  const handleBuildingChange = (buildingId: string) => {
    setValue("building_id", buildingId, { shouldValidate: true });
  };

  const onSubmit: SubmitHandler<FormSchema> = (data: FormSchema) => {
    // Submit only building_id (city_id is just for filtering)
    onLogin({ building_id: data.building_id });
  };

  return {
    form,
    onSubmit: handleSubmit(onSubmit),
    // Form state
    errors,
    isValid,
    // Selected values
    selectedCityId,
    selectedBuildingId,
    // Calculated options
    citiesOptions,
    buildingsOptions,
    // Handlers
    handleCityChange,
    handleBuildingChange,
  };
};

